import { isEmpty } from "lodash";
import { CalculatedSpace, GroupedStreetData } from "./onstreet.type";
import { Card, CardContent } from "components/ui/card";
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "components/ui/table";
import { disponibilite, perPages } from "./onstreet.const";
import { useEffect, useMemo, useState } from "react";

import { Combobox } from "@/components/form-control";
import { Input } from "components/ui/input";
import { OnStreetSkeletonView } from "./OnStreetSkeletonView";
import { OptionType } from "components/form-control/dropdown-select";
import { PaginationControls } from "./PaginationControls";
import { Search } from "lucide-react";
import { SortableHeader } from "./SortableHeader";
import { TotalAreaDisplay } from "./TotalAreaDisplay";
import { countrySymbol } from "@/constants/mapbox.const";
import { numberFormat } from "helper/format-data";
import { sortDesc } from "@/helper/array";

type StreetStatisticsViewProps = {
  groupedData: GroupedStreetData;
  isLoadingCurbZones: boolean;
};
export const StreetStatisticsView = ({ groupedData, isLoadingCurbZones }: StreetStatisticsViewProps) => {
  const [streetList, setStreetList] = useState<OptionType[]>([]);
  const [selectedStreet, setSelectedStreet] = useState<string>();
  const [policiesData, setPoliciesData] = useState<CalculatedSpace[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  // Define columns with sorting functionality
  const columns: ColumnDef<CalculatedSpace>[] = [
    {
      accessorKey: "name",
      header: ({ column }) => <SortableHeader column={column}>{disponibilite.onStreet[0]}</SortableHeader>,
      cell: ({ row }) => <div className="font-medium">{row.getValue("name")}</div>,
    },
    {
      accessorKey: "calculatedSpaces",
      header: ({ column }) => (
        <SortableHeader column={column} className="text-right">
          {disponibilite.onStreet[1]}
        </SortableHeader>
      ),
      cell: ({ row }) => <div className="text-right">{row.getValue("calculatedSpaces")}</div>,
    },
  ];

  const { totalCalculatedSpaces, totalCalculatedArea } = useMemo(() => {
    return policiesData.reduce(
      (result, item) => {
        return {
          totalCalculatedSpaces: result.totalCalculatedSpaces + item.calculatedSpaces,
          totalCalculatedArea: result.totalCalculatedArea + item.calculatedArea,
        };
      },
      {
        totalCalculatedSpaces: 0,
        totalCalculatedArea: 0,
      },
    );
  }, [policiesData]);

  useEffect(() => {
    if (isEmpty(groupedData)) {
      setStreetList([]);
      setSelectedStreet(undefined);
      setPoliciesData([]);
    } else {
      const options = Object.keys(groupedData)
        .map((key) => ({ label: key, value: key }))
        .sort((a, b) => a.label.localeCompare(b.label, countrySymbol, { sensitivity: "base" }));
      setStreetList(options);
      setSelectedStreet(options[0].label);
      const sortData = sortDesc(groupedData[options[0].label], "calculatedSpaces");
      setPoliciesData(sortData);
    }
  }, [groupedData]);

  const handleSelectStreet = (value: string) => {
    setSelectedStreet(value);
    const sortData = sortDesc(groupedData[value], "calculatedSpaces");
    setPoliciesData(sortData);
  };

  const table = useReactTable({
    data: policiesData,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: perPages[0],
      },
    },
    state: {
      sorting,
      columnFilters,
    },
  });

  const handleSearch = (input: string) => {
    table.getColumn("name")?.setFilterValue(input);
  };

  return (
    <div className="flex">
      {isLoadingCurbZones ? (
        <OnStreetSkeletonView />
      ) : (
        <>
          <div className="flex flex-col mr-14 w-96 gap-4">
            <Combobox
              value={selectedStreet}
              options={streetList}
              onValueChange={(value) => handleSelectStreet(value)}
              placeholder="Search street..."
              className="w-full"
            />
            <div className="rounded-xl border p-5">
              <div className="flex items-center">
                <span className="font-bold text-xl">{selectedStreet}</span>
              </div>
              <span className="text-13px mt-2">Street</span>
            </div>
            <div className="rounded-xl border p-5">
              <span className="text-3xl font-bold">{numberFormat(totalCalculatedSpaces)}</span>
              <span className="text-13px mt-2">&nbsp;Places calculées</span>
            </div>
            <div className="rounded-xl border p-5 flex flex-wrap items-end">
              <TotalAreaDisplay totalArea={totalCalculatedArea} />
              &nbsp;
              <span className="text-13px mt-2">Superficie</span>
            </div>
          </div>
          <div className="grow">
            <Card className="rounded-2xl space-y-4 bg-white">
              <CardContent>
                <div className="flex items-center py-4">
                  <div className="relative max-w-sm">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Filtrer par régulations..."
                      onChange={(event) => handleSearch(event.target.value)}
                      className="pl-8 w-60"
                    />
                  </div>
                </div>
                <div className="w-full rounded-md border max-h-[calc(100vh-20rem)] overflow-y-auto">
                  <Table className="m-auto border-collapse relative">
                    <TableHeader>
                      {table.getHeaderGroups().map((headerGroup) => (
                        <TableRow key={headerGroup.id}>
                          {headerGroup.headers.map((header) => {
                            return (
                              <TableHead
                                key={header.id}
                                className="sticky top-0 bg-white border-b-2 border-secondary z-10"
                              >
                                {flexRender(header.column.columnDef.header, header.getContext())}
                              </TableHead>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableHeader>
                    <TableBody className="text-table-body text-medium">
                      {table.getRowModel().rows?.length ? (
                        table.getRowModel().rows.map((row, idx) => (
                          <TableRow
                            key={row.id}
                            data-state={row.getIsSelected() && "selected"}
                            className={idx % 2 === 1 ? "bg-muted/50" : ""}
                          >
                            {row.getVisibleCells().map((cell) => (
                              <TableCell key={cell.id}>
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={columns.length} className="h-24 text-center">
                            No results.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
                <PaginationControls table={table} />
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
};
